import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["indicator"];

  connect() {
    console.log("Hot Cold controller connected");

    // Listen for activation event
    document.addEventListener("hot-cold:activate", this.activate.bind(this));
  }

  disconnect() {
    document.removeEventListener("hot-cold:activate", this.activate.bind(this));
    this.cleanup();
  }

  activate(event) {
    console.log("Hot or Cold activated!", event.detail);

    this.actualLat = event.detail.actualLat;
    this.actualLng = event.detail.actualLng;

    if (isNaN(this.actualLat) || isNaN(this.actualLng)) {
      console.error(
        "Invalid coordinates for Hot or Cold:",
        this.actualLat,
        this.actualLng
      );
      return;
    }

    this.startHotCold();
  }

  startHotCold() {
    // Create the indicator element
    this.createIndicator();

    // Start 10-second timer
    this.timeLeft = 10;
    this.updateTimer();

    this.timerInterval = setInterval(() => {
      this.timeLeft--;
      this.updateTimer();

      if (this.timeLeft <= 0) {
        this.endHotCold();
      }
    }, 1000);

    // Use tap-to-probe method for all devices - it's more fun!
    console.log("Using tap-to-probe tracking for Hot or Cold");
    this.setupTapToProbe();
  }

  createIndicator() {
    // Remove any existing indicator
    this.removeIndicator();

    // Create visible timer indicator at top-right corner
    this.indicator = document.createElement("div");
    this.indicator.id = "hot-cold-indicator";
    this.indicator.className =
      "fixed top-4 right-4 z-50 bg-gradient-to-r from-red-500 to-blue-500 text-white px-3 py-2 rounded-lg shadow-lg text-center border-2 border-white/20";

    // Prominent timer with emojis
    this.indicator.innerHTML = `
      <div class="text-xl font-bold" id="hot-cold-timer">🔥❄️ 10</div>
      <div class="text-xs opacity-90">Hot or Cold</div>
    `;

    document.body.appendChild(this.indicator);
  }

  setupTapToProbe() {
    // Find the interactive map
    const mapElement = document.querySelector(
      '[data-map-map-type-value="interactive"] [data-map-target="map"]'
    );
    if (!mapElement) {
      console.error("Could not find interactive map for Hot or Cold");
      return;
    }

    // Get the map controller
    const mapController = this.application.getControllerForElementAndIdentifier(
      mapElement.closest('[data-controller*="map"]'),
      "map"
    );

    if (!mapController || !mapController.map) {
      console.error("Could not find map controller for Hot or Cold");
      return;
    }

    this.map = mapController.map;
    this.actualLatLng = window.L.latLng(this.actualLat, this.actualLng);

    // Add click/tap listener for pin placement style interaction
    this.mapClickHandler = (e) => {
      this.showTapFeedback(e.latlng);
    };

    this.map.on("click", this.mapClickHandler);
  }

  showTapFeedback(tapLatLng) {
    // Calculate distance and get emoji
    const distance = this.map.distance(tapLatLng, this.actualLatLng);
    const emoji = this.getEmojiForDistance(distance);

    // Create temporary feedback marker at tap location
    const feedbackMarker = window.L.divIcon({
      html: `<div class="hot-cold-tap-feedback">${emoji}</div>`,
      className: "hot-cold-tap-marker",
      iconSize: [60, 30],
      iconAnchor: [30, 15],
    });

    const marker = window.L.marker(tapLatLng, { icon: feedbackMarker }).addTo(
      this.map
    );

    // Remove marker after 2 seconds
    setTimeout(() => {
      this.map.removeLayer(marker);
    }, 2000);

    // No need to update main indicator - tap feedback shows everything
  }

  getEmojiForDistance(distance) {
    // More granular distance thresholds (in meters)
    if (distance < 1000) return "🔥🔥🔥🔥🔥"; // < 1km - BLAZING
    if (distance < 2500) return "🔥🔥🔥🔥"; // < 2.5km - Very hot
    if (distance < 5000) return "🔥🔥🔥"; // < 5km - Hot
    if (distance < 10000) return "🔥🔥"; // < 10km - Warm
    if (distance < 20000) return "🔥"; // < 20km - Slightly warm
    if (distance < 35000) return "❄️"; // < 35km - Cool
    if (distance < 50000) return "🧊"; // < 50km - Cold
    if (distance < 75000) return "🧊🧊"; // < 75km - Very cold
    return "🧊🧊🧊"; // 75km+ - FREEZING
  }

  updateTimer() {
    const timerElement = document.getElementById("hot-cold-timer");
    if (timerElement) {
      timerElement.textContent = `${this.timeLeft}s`;
    }
  }

  endHotCold() {
    console.log("Hot or Cold ended");
    this.cleanup();
  }

  cleanup() {
    // Clear timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    // Remove mouse listener (hover mode)
    if (this.map && this.mouseMoveHandler) {
      this.map.off("mousemove", this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }

    // Remove map click listeners (tap-to-probe mode)
    if (this.map && this.mapClickHandler) {
      this.map.off("click", this.mapClickHandler);
      this.mapClickHandler = null;
    }

    // Remove indicator
    this.removeIndicator();
  }

  removeIndicator() {
    const existingIndicator = document.getElementById("hot-cold-indicator");
    if (existingIndicator) {
      existingIndicator.remove();
    }
  }
}
